import { useState, useCallback, useRef } from "react";
import { FilterInfo } from "@lhd/features/Apps/AppList/ManagementAppSheet/ManagementAppSheetList/AppSheetComponents/FilterIcon";
import { clearSortFromOtherColumns } from "@lhd/utility";
import { isEqual, omit } from "lodash";

export interface UseCascadingFilterProps<T> {
  columns: T[];
  getColumnId: (col: T) => string;
  statusKeys?: string[];
  categoryKeys?: string[];
}

const isEmptyFilterState = (val: any) => {
  if (!val) return true;
  const checkVal = omit(val, ["sort", "filterOptions", "isCheckAll"]);
  if (Object.keys(checkVal).length === 0) return true;

  let hasFilter = false;
  if ("filter" in checkVal && checkVal.filter !== undefined) {
    hasFilter = true;
  }
  const hasSearch = !!checkVal.search;
  const hasSpecial = !!checkVal.filter_special_empty;

  return !hasFilter && !hasSearch && !hasSpecial;
};

export const useCascadingFilter = <T>({
  columns,
  getColumnId,
  statusKeys = [],
  categoryKeys = [],
}: UseCascadingFilterProps<T>) => {
  const [internalFilterInfo, setInternalFilterInfo] = useState<FilterInfo>({
    open: false,
    columnId: null,
    position: { x: 0, y: 0 },
    filter: {},
    filterOrder: [],
  });

  const columnsRef = useRef(columns);
  columnsRef.current = columns;
  const getColumnIdRef = useRef(getColumnId);
  getColumnIdRef.current = getColumnId;
  const statusKeysRef = useRef(statusKeys);
  statusKeysRef.current = statusKeys;
  const categoryKeysRef = useRef(categoryKeys);
  categoryKeysRef.current = categoryKeys;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setFilterInfo = useCallback(
    (newFilterInfo: React.SetStateAction<FilterInfo>) => {
      setInternalFilterInfo((prev) => {
        const nextInfo =
          typeof newFilterInfo === "function"
            ? newFilterInfo(prev)
            : newFilterInfo;

        if (nextInfo.filter && nextInfo.filter !== prev.filter) {
          const changedKeys: string[] = [];
          const prevFilter = prev.filter || {};
          const nextFilter = nextInfo.filter || {};

          const allKeys = new Set([
            ...Object.keys(prevFilter),
            ...Object.keys(nextFilter),
          ]);

          for (const key of Array.from(allKeys)) {
            const wasEmpty =
              !(key in prevFilter) || isEmptyFilterState(prevFilter[key]);
            const isEmptyNow =
              !(key in nextFilter) || isEmptyFilterState(nextFilter[key]);

            if (wasEmpty && isEmptyNow) {
              continue;
            }

            const isDifferent = !isEqual(
              omit(nextFilter[key] || {}, ["sort", "filterOptions"]),
              omit(prevFilter[key] || {}, ["sort", "filterOptions"]),
            );

            if (isDifferent) {
              changedKeys.push(key);
            }
          }

          if (changedKeys.length === 1) {
            const changedKey = changedKeys[0];
            const currentFilterOrder = prev.filterOrder || [];
            let changedIndex = currentFilterOrder.indexOf(changedKey);
            let nextFilterOrder = [...currentFilterOrder];

            if (changedIndex === -1) {
              changedIndex = nextFilterOrder.length;
              if (!isEmptyFilterState(nextFilter[changedKey])) {
                nextFilterOrder.push(changedKey);
              }
            } else {
              const baseOrder = nextFilterOrder.slice(0, changedIndex + 1);
              if (isEmptyFilterState(nextFilter[changedKey])) {
                baseOrder.pop();
              }
              // Also keep any active slicer keys (status and category) that were downstream
              const downstreamSlicerKeys = currentFilterOrder
                .slice(changedIndex + 1)
                .filter(
                  (k) =>
                    (statusKeysRef.current.includes(k) ||
                      categoryKeysRef.current.includes(k)) &&
                    !isEmptyFilterState(nextFilter[k]),
                );
              nextFilterOrder = [...baseOrder, ...downstreamSlicerKeys];
            }

            let updatedFilter = { ...nextInfo.filter };

            // If the new column has sort, clear sort from other columns
            if (nextFilter[changedKey]?.sort) {
              updatedFilter = clearSortFromOtherColumns(
                updatedFilter,
                changedKey,
              );
            }

            for (const key of Object.keys(updatedFilter)) {
              // Never clear slicer filters (status and category) as downstream cascades
              if (
                statusKeysRef.current.includes(key) ||
                categoryKeysRef.current.includes(key)
              ) {
                continue;
              }

              const currentKeyIndex = currentFilterOrder.indexOf(key);
              // Only clear filters that were downstream of the changed column
              if (currentKeyIndex > changedIndex) {
                const keepState: any = {};
                if (updatedFilter[key]?.sort) {
                  keepState.sort = updatedFilter[key].sort;
                }
                // Keep filterOptions so we don't have to refetch them
                if (updatedFilter[key]?.filterOptions) {
                  keepState.filterOptions = updatedFilter[key].filterOptions;
                }

                if (Object.keys(keepState).length > 0) {
                  updatedFilter[key] = keepState;
                } else {
                  delete updatedFilter[key];
                }
              }
            }
            return {
              ...nextInfo,
              filter: updatedFilter,
              filterOrder: nextFilterOrder,
            };
          } else if (changedKeys.length > 1) {
            const currentFilterOrder = prev.filterOrder || [];
            let nextFilterOrder = [...currentFilterOrder];
            const updatedFilter = { ...nextInfo.filter };

            const statusKeysInChanged = changedKeys.filter((k) =>
              statusKeysRef.current.includes(k),
            );

            // If multiple status keys changed, keep only the one with non-empty filter
            // and clear all others to prevent multiple API calls
            if (statusKeysInChanged.length > 1) {
              const activeStatusKey = statusKeysInChanged.find(
                (k) => nextFilter[k] && !isEmptyFilterState(nextFilter[k]),
              );

              // Remove all status keys except the active one from updatedFilter
              for (const statusKey of statusKeysInChanged) {
                if (statusKey !== activeStatusKey) {
                  delete updatedFilter[statusKey];
                  nextFilterOrder = nextFilterOrder.filter(
                    (k) => k !== statusKey,
                  );
                }
              }

              // Ensure the active status key is in the filter order if it has a filter
              if (
                activeStatusKey &&
                !isEmptyFilterState(nextFilter[activeStatusKey])
              ) {
                if (!nextFilterOrder.includes(activeStatusKey)) {
                  nextFilterOrder.push(activeStatusKey);
                }
              }

              for (const catKey of categoryKeysRef.current) {
                if (
                  nextFilter[catKey] &&
                  !isEmptyFilterState(nextFilter[catKey]) &&
                  !nextFilterOrder.includes(catKey)
                ) {
                  nextFilterOrder.push(catKey);
                }
              }
            } else {
              // Normal multi-key handling for non-status keys
              for (const key of changedKeys) {
                if (isEmptyFilterState(nextFilter[key])) {
                  nextFilterOrder = nextFilterOrder.filter((k) => k !== key);
                } else if (!nextFilterOrder.includes(key)) {
                  nextFilterOrder.push(key);
                }
              }
            }

            return {
              ...nextInfo,
              filter: updatedFilter,
              filterOrder: nextFilterOrder,
            };
          }
        }
        return nextInfo;
      });
    },
    [],
  );

  return {
    filterInfo: internalFilterInfo,
    setFilterInfo,
    setInternalFilterInfo,
  };
};

export const getUpstreamActiveFilters = <T>(
  activeFilters: Record<string, string[]>,
  columns: T[],
  getColumnId: (col: T) => string,
  currentColumnId: string | null,
  filterOrder?: string[],
): Record<string, string[]> => {
  if (filterOrder && filterOrder.length > 0) {
    const targetIndex = filterOrder.indexOf(String(currentColumnId));
    if (targetIndex !== -1) {
      const filteredResult: Record<string, string[]> = {};
      for (const key of Object.keys(activeFilters)) {
        const keyIndex = filterOrder.indexOf(key);
        if (keyIndex !== -1 && keyIndex < targetIndex) {
          filteredResult[key] = activeFilters[key];
        }
      }
      return filteredResult;
    }

    // If the currentColumnId is not yet in filterOrder, it means we are opening its filter
    // for the first time, so all currently active filters in filterOrder are upstream of it.
    const filteredResult: Record<string, string[]> = {};
    for (const key of Object.keys(activeFilters)) {
      if (filterOrder.includes(key)) {
        filteredResult[key] = activeFilters[key];
      }
    }
    return filteredResult;
  }

  const targetIndex = columns.findIndex(
    (col) => getColumnId(col) === String(currentColumnId),
  );

  if (targetIndex !== -1) {
    const filteredResult: Record<string, string[]> = {};
    for (const key of Object.keys(activeFilters)) {
      const keyIndex = columns.findIndex((col) => getColumnId(col) === key);
      // Only include filters for columns to the left of the requested column
      if (keyIndex !== -1 && keyIndex < targetIndex) {
        filteredResult[key] = activeFilters[key];
      }
    }
    return filteredResult;
  }

  return activeFilters;
};
